import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Container } from '../../../components'
import {
    IconButton,
    Modal,
    Paper,
    Slide,
    Step,
    StepLabel,
    Stepper,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { InspectionDetailStep } from './InspectionDetailStep'
import {
    Inspection,
    InspectionType,
    Schedule,
    User,
    Unit,
} from '../../../models'
import { CreateInspectionAxiosReq, createInspection } from '../../../store'
import { useAppDispatch } from '../../../hooks'
import { toast } from 'react-toastify'
import { toYYYYMMDD } from '../../../helpers'
import { AssignInspectionStep } from './AssignInspectionStep'
import { SelectOfflineUnits } from './SelectOfflineUnits'
import { typographyStyle } from '../../../theme/typographyStyle'

// Create a shared context for assignment progress
export interface AssignmentProgress {
    inspectionId: number
    progress: number // 0-100 percentage
    status: 'pending' | 'in_progress' | 'completed' | 'error'
    message?: string
}

// Create a context for the progress state
export const AssignmentProgressContext = React.createContext<{
    progress: AssignmentProgress
    updateProgress: (data: AssignmentProgress) => void
}>({
    progress: {
        inspectionId: 0,
        progress: 0,
        status: 'pending',
        message: 'Waiting to start assignment process',
    },
    updateProgress: () => {},
})

interface Props {
    open: boolean
    scheduleList?: Schedule[]
    inspectionTypeList: InspectionType[]
    userList: User[]
    onClose: () => void
    onInspectionCreated?: (newInspection: Inspection) => void
}

export const CreateInspectionModal = (props: Props) => {
    const {
        open,
        onClose,
        scheduleList,
        inspectionTypeList,
        userList,
        onInspectionCreated,
    } = props

    const [
        createdInspection,
        setCreatedInspection,
    ] = useState<Inspection | null>(null)

    const [activeStep, setActiveStep] = useState<number>(0)
    const [wsStatus, setWsStatus] = useState<string>('Not connected')
    const [wsMessages, setWsMessages] = useState<string[]>([])
    const wsRef = useRef<WebSocket | null>(null)
    const [selectedUnits, setSelectedUnits] = useState<Unit[]>([])
    const [offlineInspection, setOfflineInspection] = useState<boolean>(false)
    const [moveOutInspections, setMoveOutInspections] = useState<boolean>(false)

    // Assignment progress state
    const [
        assignmentProgress,
        setAssignmentProgress,
    ] = useState<AssignmentProgress>({
        inspectionId: 0,
        progress: 0,
        status: 'pending',
        message: 'Waiting to start assignment process',
    })

    // Update progress function for the context
    const updateProgress = useCallback((data: AssignmentProgress) => {
        console.log('Updating progress:', data)
        setAssignmentProgress(data)
    }, [])
    const theme = useTheme()

    const dispatch = useAppDispatch()

    // Function to establish a WebSocket connection
    const establishConnection = () => {
        // Close any existing connection
        if (wsRef.current) {
            wsRef.current.close(1000, 'New connection requested')
        }

        // Only establish connection if we have a valid inspection ID
        if (!createdInspection?.id) {
            console.log(
                'No valid inspection ID available, skipping WebSocket connection',
            )
            setWsStatus('Not connected - No inspection ID')
            return
        }

        setWsStatus('Connecting...')
        setWsMessages([])

        try {
            // Get the WebSocket base URL from environment variables
            const WEBSOCKET_BASE_URL =
                process.env.REACT_APP_WEBSOCKET_BASE_URL ||
                'ws://localhost:8000'

            // Use inspection ID
            const inspectionId = createdInspection.id
            console.log(
                `Establishing WebSocket connection for inspection: ${inspectionId}`,
            )

            const url = `${WEBSOCKET_BASE_URL}inspection/assignment/${inspectionId}/`

            console.log(`Attempting WebSocket connection to: ${url}`)
            const socket = new WebSocket(url)
            wsRef.current = socket

            socket.onopen = () => {
                console.log(
                    'WebSocket connection opened for inspection:',
                    inspectionId,
                )
                setWsStatus('Connected - Socket Open')
                setWsMessages((prev) => [
                    ...prev,
                    `Connection opened for inspection: ${inspectionId}`,
                ])

                // Send a ping message to make sure connection is working
                try {
                    socket.send(
                        JSON.stringify({
                            type: 'ping',
                            message: 'Connection established',
                        }),
                    )
                } catch (e) {
                    console.error('Error sending initial ping:', e)
                }
            }

            socket.onmessage = (event) => {
                console.log('WebSocket message received:', event.data)
                setWsMessages((prev) => [...prev, `Received: ${event.data}`])

                try {
                    const data = JSON.parse(event.data)
                    console.log('Parsed WebSocket message:', data)

                    // Handle assignment progress message
                    if (data.type === 'assignment_progress') {
                        console.log(
                            'Assignment progress update received:',
                            data,
                        )

                        // Update our progress context with the new data
                        updateProgress({
                            inspectionId: parseInt(data.inspection_id),
                            progress: parseFloat(data.progress) || 0,
                            status: data.status || 'pending',
                            message: data.message || '',
                        })

                        // If we received a completed status, refresh the inspection data
                        if (data.status === 'completed') {
                            console.log(
                                'Assignment process completed, refreshing data',
                            )
                        }
                    }

                    setWsStatus(
                        `Connected - Got response: ${JSON.stringify(
                            data,
                        ).substring(0, 50)}...`,
                    )
                } catch (e) {
                    console.error('Error parsing WebSocket message:', e)
                    setWsStatus(`Connected - Got message: ${event.data}`)
                }
            }

            socket.onerror = (error) => {
                console.error('WebSocket error:', error)
                setWsStatus('Error - See console for details')
                setWsMessages((prev) => [...prev, 'Connection error occurred'])
            }

            socket.onclose = (event) => {
                console.log(
                    `WebSocket closed with code: ${event.code}, reason: ${
                        event.reason || 'No reason provided'
                    }`,
                )
                setWsStatus(
                    `Closed - Code ${event.code} ${
                        event.reason ? `(${event.reason})` : ''
                    }`,
                )
                setWsMessages((prev) => [
                    ...prev,
                    `Connection closed: ${event.code} ${event.reason || ''}`,
                ])
                wsRef.current = null
            }
        } catch (error) {
            console.error('Failed to create WebSocket:', error)
            setWsStatus(`Failed to create connection: ${error}`)
        }
    }

    // Initialize WebSocket when modal is opened and we have an inspection ID
    useEffect(() => {
        if (open && createdInspection?.id) {
            console.log(
                'Establishing WebSocket connection for inspection:',
                createdInspection.id,
            )
            // Update our assignment progress state with the inspection ID
            setAssignmentProgress((prev) => ({
                ...prev,
                inspectionId: createdInspection.id,
            }))

            // Establish a WebSocket connection
            establishConnection()
        } else if (!createdInspection?.id && wsRef.current) {
            // Close WebSocket if we don't have a valid inspection ID
            console.log(
                'No valid inspection ID, closing any existing WebSocket connection',
            )
            wsRef.current.close(1000, 'No valid inspection ID')
            wsRef.current = null
        }

        return () => {
            if (wsRef.current) {
                console.log(
                    'Closing WebSocket on component unmount or inspection change',
                )
                wsRef.current.close(
                    1000,
                    'Component unmounting or inspection changed',
                )
                wsRef.current = null
            }
        }
    }, [open, createdInspection?.id])

    const handleCreate = (
        name: string,
        startDate: Date,
        endDate: Date,
        inspectionTypeId: number,
        scheduleId: number,
        damageGoal: number,
        shareWithTenant: boolean,
    ) => {
        const req: CreateInspectionAxiosReq = {
            body: {
                name: name,
                // Date-only so schedule linking resolves to property day bounds.
                start_date: toYYYYMMDD(startDate),
                end_date: toYYYYMMDD(endDate),
                inspection_type: inspectionTypeId,
                damage_goal: damageGoal,
                share_with_tenant: shareWithTenant,
            },
        }
        if (scheduleId !== -1) {
            req.body.schedule = scheduleId
        }

        dispatch(createInspection(req))
            .then((res) => {
                console.log('Inspection created successfully:', res.data)

                if (!res.data || !res.data.id) {
                    console.error('Created inspection missing ID')
                    toast.error('Created inspection is missing ID')
                    return
                }

                // Set the created inspection with the response data
                setCreatedInspection(res.data)
                // Notify parent component to add the new inspection to the list
                if (onInspectionCreated && res?.data) {
                    onInspectionCreated(res.data)
                }

                // Setup the initial progress
                updateProgress({
                    inspectionId: res.data.id,
                    progress: 0,
                    status: 'pending',
                    message: 'Waiting to start assignment process',
                })

                // WebSocket connection will be established by the useEffect hook
                // No need to call establishTestConnection directly
            })
            .catch((error) => {
                console.error('Error creating inspection:', error)
                toast.error('There was a problem creating your inspection')
            })

        setActiveStep(activeStep + 1)
    }

    useEffect(() => {
        if (!open) {
            setCreatedInspection(null)
            // Close the WebSocket when the modal is closed
            if (wsRef.current) {
                wsRef.current.close(1000, 'Modal closed')
                wsRef.current = null
            }
        }
    }, [open])

    // Provide the current assignment progress to child components
    const progressContextValue = {
        progress: assignmentProgress,
        updateProgress,
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper style={{}}>
                    {/* Modal Root */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            width: 'calc(100vw - 200px)',
                            maxWidth: 'calc(100vw - 200px)',
                            height: 'calc(100vw - 200px)',
                            maxHeight: 'calc(100vh - 200px)',
                            flex: 1,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <Container
                            style={{
                                padding: theme.spacing(2),
                                borderBottom: `1px solid ${theme.palette.grey[400]}`,
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 700,
                                }}
                            >
                                Create Inspection
                            </span>

                            <div style={{ flex: 1 }} />

                            <IconButton
                                onClick={() => {
                                    setActiveStep(0)
                                    setCreatedInspection(null)
                                    onClose()
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Container>
                        {/* Body */}

                        <AssignmentProgressContext.Provider
                            value={progressContextValue}
                        >
                            <Container
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                                }}
                            >
                                {activeStep === 0 && (
                                    <InspectionDetailStep
                                        theme={theme}
                                        scheduleList={scheduleList}
                                        inspectionTypeList={inspectionTypeList}
                                        completeStep={(
                                            name,
                                            startDate,
                                            endDate,
                                            inspectionTypeId,
                                            scheduleId,
                                            damageGoal,
                                            shareWithTenant,
                                        ) => {
                                            handleCreate(
                                                name,
                                                startDate,
                                                endDate,
                                                inspectionTypeId,
                                                scheduleId,
                                                damageGoal,
                                                shareWithTenant,
                                            )
                                        }}
                                    />
                                )}

                                {activeStep === 1 &&
                                    createdInspection !== null && (
                                        <AssignInspectionStep
                                            theme={theme}
                                            inspection={createdInspection}
                                            userList={userList}
                                            selectedUnits={selectedUnits}
                                            setSelectedUnits={setSelectedUnits}
                                            offlineInspection={
                                                offlineInspection
                                            }
                                            setOfflineInspection={
                                                setOfflineInspection
                                            }
                                            moveOutInspections={
                                                moveOutInspections
                                            }
                                            setMoveOutInspections={
                                                setMoveOutInspections
                                            }
                                            completeStep={() => {
                                                setActiveStep(activeStep + 1)
                                            }}
                                        />
                                    )}

                                {activeStep === 2 &&
                                    createdInspection !== null && (
                                        <SelectOfflineUnits
                                            theme={theme}
                                            inspection={createdInspection}
                                            assignedUnits={selectedUnits}
                                            onClose={onClose}
                                        />
                                    )}
                            </Container>
                        </AssignmentProgressContext.Provider>

                        {/* Footer */}
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Stepper activeStep={activeStep}>
                                <Step completed={activeStep > 0}>
                                    <StepLabel>Inspection Details</StepLabel>
                                </Step>

                                <Step completed={activeStep > 1}>
                                    <StepLabel>Assign Units</StepLabel>
                                </Step>
                                {offlineInspection && (
                                    <Step completed={activeStep > 2}>
                                        <StepLabel>Offline Units</StepLabel>
                                    </Step>
                                )}
                            </Stepper>
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
